/**
 * Dev/staging-only progress seeding for store captures.
 *
 * Every screenshot renderer drives a freshly-launched browser profile, so the menu it captures
 * always reads `0 / 241 Wörter · ★ 0/25` with an empty rocket track — the least persuasive frame
 * in the set (specs/store-presence.md §2 R2). `?seed=<n>` marks the first `n` masterable words of
 * the ACTIVE lesson as mastered, so the journey bar, the star row and the rocket all show a player
 * who is under way.
 *
 * A seed that covers the WHOLE journey additionally stamps the ABC Planet, which is what makes the
 * `9-celebration` store capture reachable — see `maybeSeedProgress`.
 *
 * Gated by `devParam`, so it is inert on production exactly like `?survival` and `?buy`. It only
 * ever WRITES progress — there is no path here that can take a real player's progress away.
 */
import { packKey } from '@/data/packs';
import { getActivePack } from '@/game/packs';
import { devParam } from '@/game/dev';
import { masterableWords } from './lessonStats';
import { emptyMastery, flushMastery, loadMastery, saveMastery, type WordStat } from './store';
import { MASTER_BOX, MIN_SESSIONS_TO_MASTER } from './config';

/**
 * Bare `?seed` with no value. 31 of a ~250-word journey is 3 of 25 stars — the "a few weeks in"
 * feel the store-presence spec asks the menu shot to show.
 */
const DEFAULT_SEEDED = 31;

/** Lesson keys already seeded this page load, so a scene restart doesn't redo the work. */
const seeded = new Set<string>();

function masteredStat(ts: number): WordStat {
  return {
    box: MASTER_BOX,
    state: 'mastered',
    seen: 6,
    correct: 6,
    wrong: 0,
    streak: 4,
    bestStreak: 4,
    introducedAt: ts,
    lastSeenAt: ts,
    masteredAt: ts,
    sessionsSeen: MIN_SESSIONS_TO_MASTER,
    lastSession: MIN_SESSIONS_TO_MASTER,
    lastBoardSeq: 0,   // "not seen recently" — seeded progress has no board history to space against
  };
}

/**
 * Seed the active lesson's journey when `?seed[=n]` is present in a dev environment.
 * Call it BEFORE constructing the ReadingProgression that will be drawn — the engine snapshots
 * the stored blob in its constructor.
 *
 * `?seed=999` (any n at or above the lesson's size) seeds the journey to completion and stamps
 * `abcPlanetReachedAt`, leaving `celebrationShownAt` null — so the journey is finished AND its
 * celebration is still owed, and MenuScene's backstop hands straight over to CelebrationScene on
 * the next load. That is the capture route for `9-celebration`; the alternative was playing a
 * ~250-word journey to its end inside a screenshot renderer.
 */
export function maybeSeedProgress(): void {
  const raw = devParam('seed');
  if (raw === null) return;
  const n = raw === '' ? DEFAULT_SEEDED : Number.parseInt(raw, 10);
  if (!Number.isFinite(n) || n <= 0) return;

  const pool = getActivePack();
  const key = packKey(pool);
  if (seeded.has(key)) return;
  seeded.add(key);

  const words = masterableWords(pool);
  const data = loadMastery(key) ?? emptyMastery();
  const ts = Date.now();
  for (const w of words.slice(0, Math.min(n, words.length))) {
    const id = typeof w.id === 'string' ? w.id : w.text;
    data.words[id] = masteredStat(ts);
  }
  // Mirrors ReadingProgression.checkAbcPlanet: the planet is "all of THIS lesson mastered", and
  // the lesson's own size is the target — reading pools are 250 words, arcade lessons are smaller.
  // `??=` so a real stamp already in the blob keeps its original timestamp.
  if (words.length > 0 && n >= words.length) {
    data.profile.abcPlanetReachedAt ??= ts;
  }
  // A journey this far along has been played before; leave the UFO counters alone so the menu
  // stays deterministic (they are only granted at beginSession, never on the menu).
  data.profile.firstPlayedAt ??= ts;
  data.profile.lastPlayedAt ??= ts;
  data.sessionCounter = Math.max(data.sessionCounter, MIN_SESSIONS_TO_MASTER);
  saveMastery(key, data);
  flushMastery();   // the capture may navigate before the coalesced write would have landed
}
